import React, { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, ArrowRight, ArrowLeft, Check, User, Briefcase, FileCheck, Users } from 'lucide-react';

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance & Banking',
  'Manufacturing',
  'Retail & E-commerce',
  'Real Estate',
  'Education',
  'Construction',
  'Transportation & Logistics',
  'Energy & Utilities',
  'Media & Entertainment',
  'Agriculture',
  'Hospitality & Tourism',
  'Professional Services',
  'Other'
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain',
  'Australia', 'Japan', 'China', 'India', 'Brazil', 'Mexico', 'South Korea', 'Singapore',
  'Netherlands', 'Switzerland', 'Sweden', 'Norway', 'Denmark', 'Belgium', 'Austria',
  'Ireland', 'Portugal', 'Poland', 'Czech Republic', 'South Africa', 'UAE', 'Saudi Arabia',
  'Israel', 'New Zealand', 'Argentina', 'Chile', 'Colombia', 'Thailand', 'Malaysia',
  'Indonesia', 'Philippines', 'Vietnam', 'Taiwan', 'Hong Kong', 'Other'
];

const initialFormData = {
  // Step 1: Basic Info
  email: '',
  password: '',
  confirmPassword: '',
  name: '',
  role: 'buyer',
  // Step 2: Company Info
  company_name: '',
  street_address: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
  phone: '',
  company_email: '',
  website: '',
  industry: '',
  description: '',
  // Step 3: Terms
  agreed_to_terms: false,
  agreed_to_privacy: false,
  list_visible: false, // Default OFF - let buyer decide to opt-in
};

const AuthPage = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const totalSteps = 3;

  // Reset form when switching between login and register
  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setCurrentStep(1);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('auth.validation.name_required');
    if (!formData.email.trim()) newErrors.email = t('auth.validation.email_required');
    if (!formData.password) newErrors.password = t('auth.validation.password_required');
    if (formData.password.length < 6) newErrors.password = t('auth.validation.password_min');
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t('auth.validation.password_mismatch');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = t('auth.validation.company_required');
    if (!formData.street_address.trim()) newErrors.street_address = t('auth.validation.address_required');
    if (!formData.city.trim()) newErrors.city = t('auth.validation.city_required');
    if (!formData.country) newErrors.country = t('auth.validation.country_required');
    if (!formData.phone.trim()) newErrors.phone = t('auth.validation.phone_required');
    if (!formData.company_email.trim()) newErrors.company_email = t('auth.validation.company_email_required');
    if (!formData.industry) newErrors.industry = t('auth.validation.industry_required');
    if (!formData.description.trim()) newErrors.description = t('auth.validation.description_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.agreed_to_terms) newErrors.agreed_to_terms = t('auth.validation.terms_required');
    if (!formData.agreed_to_privacy) newErrors.agreed_to_privacy = t('auth.validation.privacy_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        login(response.data.access_token, response.data.user);
        toast.success('Logged in successfully!');
        navigate('/');
      } catch (error) {
        console.error('Login error:', error);
        toast.error(error.response?.data?.detail || 'Login failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Registration flow
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        company: formData.company_name,
        company_info: {
          company_name: formData.company_name,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state_province,
          postal_code: formData.postal_code,
          country: formData.country,
          phone: formData.phone,
          company_email: formData.company_email,
          website: formData.website,
          industry: formData.industry,
          description: formData.description,
        },
        agreed_to_terms: formData.agreed_to_terms,
        agreed_to_privacy: formData.agreed_to_privacy,
        list_visible: formData.list_visible,
      };

      const response = await axios.post(`${API}/auth/register`, payload);
      login(response.data.access_token, response.data.user);
      toast.success('Account created successfully! Welcome to TradeHub.');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step, idx) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
            currentStep >= step 
              ? 'bg-primary border-primary text-white' 
              : 'border-slate-300 text-slate-400'
          }`}>
            {currentStep > step ? <Check className="w-5 h-5" /> : step}
          </div>
          {idx < 2 && (
            <div className={`w-16 h-1 mx-2 rounded ${
              currentStep > step ? 'bg-primary' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStepTitle = () => {
    const titles = {
      1: { icon: User, title: t('auth.step1_title'), subtitle: t('auth.step1_subtitle') },
      2: { icon: Briefcase, title: t('auth.step2_title'), subtitle: t('auth.step2_subtitle') },
      3: { icon: FileCheck, title: t('auth.step3_title'), subtitle: t('auth.step3_subtitle') }
    };
    const { icon: Icon, title, subtitle } = titles[currentStep];
    return (
      <div className="text-center mb-6">
        <Icon className="h-8 w-8 text-primary mx-auto mb-2" />
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-600">{subtitle}</p>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">{t('auth.full_name')} *</Label>
        <Input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={errors.name ? 'border-red-500' : ''}
          data-testid="auth-name-input"
        />
        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
      </div>
      <div>
        <Label htmlFor="email">{t('auth.email_address')} *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className={errors.email ? 'border-red-500' : ''}
          data-testid="auth-email-input"
        />
        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
      </div>
      <div>
        <Label htmlFor="role">{t('auth.role')} *</Label>
        <Select
          value={formData.role}
          onValueChange={(value) => setFormData({ ...formData, role: value })}
        >
          <SelectTrigger data-testid="auth-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="buyer">{t('auth.role_buyer')}</SelectItem>
            <SelectItem value="seller">{t('auth.role_seller')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="password">{t('auth.password')} *</Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          className={errors.password ? 'border-red-500' : ''}
          data-testid="auth-password-input"
        />
        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
      </div>
      <div>
        <Label htmlFor="confirmPassword">{t('auth.confirm_password')} *</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          className={errors.confirmPassword ? 'border-red-500' : ''}
          data-testid="auth-confirm-password-input"
        />
        {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="company_name">{t('auth.company_org_name')} *</Label>
        <Input
          id="company_name"
          type="text"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          className={errors.company_name ? 'border-red-500' : ''}
          data-testid="company-name-input"
        />
        {errors.company_name && <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>}
      </div>
      <div>
        <Label htmlFor="street_address">{t('auth.street_address')} *</Label>
        <Input
          id="street_address"
          type="text"
          value={formData.street_address}
          onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
          className={errors.street_address ? 'border-red-500' : ''}
          placeholder="123 Business Street"
          data-testid="street-address-input"
        />
        {errors.street_address && <p className="text-red-500 text-sm mt-1">{errors.street_address}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="city">{t('auth.city')} *</Label>
          <Input
            id="city"
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className={errors.city ? 'border-red-500' : ''}
            data-testid="city-input"
          />
          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
        </div>
        <div>
          <Label htmlFor="state_province">{t('auth.state_province')}</Label>
          <Input
            id="state_province"
            type="text"
            value={formData.state_province}
            onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
            data-testid="state-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="postal_code">{t('auth.postal_code')}</Label>
          <Input
            id="postal_code"
            type="text"
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            data-testid="postal-code-input"
          />
        </div>
        <div>
          <Label htmlFor="country">{t('auth.country')} *</Label>
          <Select
            value={formData.country}
            onValueChange={(value) => setFormData({ ...formData, country: value })}
          >
            <SelectTrigger className={errors.country ? 'border-red-500' : ''} data-testid="country-select">
              <SelectValue placeholder={t('auth.select_country')} />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((country) => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">{t('auth.phone_number')} *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className={errors.phone ? 'border-red-500' : ''}
            placeholder="+1 (555) 123-4567"
            data-testid="phone-input"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>
        <div>
          <Label htmlFor="company_email">{t('auth.company_email')} *</Label>
          <Input
            id="company_email"
            type="email"
            value={formData.company_email}
            onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
            className={errors.company_email ? 'border-red-500' : ''}
            placeholder="contact@company.com"
            data-testid="company-email-input"
          />
          {errors.company_email && <p className="text-red-500 text-sm mt-1">{errors.company_email}</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="website">{t('auth.website')}</Label>
        <Input
          id="website"
          type="url"
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://www.company.com"
          data-testid="website-input"
        />
      </div>
      <div>
        <Label htmlFor="industry">{t('auth.industry')} *</Label>
        <Select
          value={formData.industry}
          onValueChange={(value) => setFormData({ ...formData, industry: value })}
        >
          <SelectTrigger className={errors.industry ? 'border-red-500' : ''} data-testid="industry-select">
            <SelectValue placeholder={t('auth.select_industry')} />
          </SelectTrigger>
          <SelectContent>
            {INDUSTRIES.map((industry) => (
              <SelectItem key={industry} value={industry}>{industry}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry}</p>}
      </div>
      <div>
        <Label htmlFor="description">{t('auth.description_interest')} *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className={errors.description ? 'border-red-500' : ''}
          placeholder={formData.role === 'buyer' 
            ? t('auth.buyer_placeholder')
            : t('auth.seller_placeholder')
          }
          rows={3}
          data-testid="description-input"
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-slate-50 rounded-lg p-4 max-h-[250px] overflow-y-auto text-sm text-slate-700">
        <h3 className="font-semibold mb-2">{t('auth.terms_title')}</h3>
        <p className="mb-4">
          {t('auth.terms_intro')}
        </p>
        <ul className="list-disc ml-5 space-y-2">
          <li>{t('auth.terms_1')}</li>
          <li>{t('auth.terms_2')}</li>
          <li>{t('auth.terms_3')}</li>
          <li>{t('auth.terms_4')}</li>
          <li>{t('auth.terms_5')}</li>
          <li>{t('auth.terms_6')}</li>
        </ul>
      </div>
      
      <div className="bg-slate-50 rounded-lg p-4 max-h-[250px] overflow-y-auto text-sm text-slate-700">
        <h3 className="font-semibold mb-2">{t('auth.privacy_title')}</h3>
        <p className="mb-4">
          {t('auth.privacy_intro')}
        </p>
        <ul className="list-disc ml-5 space-y-2">
          <li>{t('auth.privacy_1')}</li>
          <li>{t('auth.privacy_2')}</li>
          <li>{t('auth.privacy_3')}</li>
          <li>{t('auth.privacy_4')}</li>
          <li>{t('auth.privacy_5')}</li>
          <li>{t('auth.privacy_6')}</li>
        </ul>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="terms"
            checked={formData.agreed_to_terms}
            onCheckedChange={(checked) => setFormData({ ...formData, agreed_to_terms: checked })}
            data-testid="terms-checkbox"
          />
          <label htmlFor="terms" className="text-sm cursor-pointer">
            {t('auth.accept_terms')} <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">{t('auth.terms_title')}</a> *
          </label>
        </div>
        {errors.agreed_to_terms && <p className="text-red-500 text-sm ml-6">{errors.agreed_to_terms}</p>}
        
        <div className="flex items-start space-x-3">
          <Checkbox
            id="privacy"
            checked={formData.agreed_to_privacy}
            onCheckedChange={(checked) => setFormData({ ...formData, agreed_to_privacy: checked })}
            data-testid="privacy-checkbox"
          />
          <label htmlFor="privacy" className="text-sm cursor-pointer">
            {t('auth.accept_privacy')} <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">{t('auth.privacy_title')}</a> *
          </label>
        </div>
        {errors.agreed_to_privacy && <p className="text-red-500 text-sm ml-6">{errors.agreed_to_privacy}</p>}
      </div>

      {/* List Visibility Opt-in with Discount */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
        <div className="flex items-start gap-3">
          <Checkbox
            id="list_visible"
            checked={formData.list_visible}
            onCheckedChange={(checked) => setFormData({ ...formData, list_visible: checked })}
          />
          <div>
            <label htmlFor="list_visible" className="text-sm font-medium cursor-pointer">
              {t('auth.list_visible')}
            </label>
            <p className="text-xs text-slate-500 mt-1">
              {t('auth.list_visible_desc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="email">{t('auth.email')}</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="auth-email-input"
        />
      </div>
      <div>
        <Label htmlFor="password">{t('auth.password')}</Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required
          data-testid="auth-password-input"
        />
      </div>
      <div className="flex justify-end">
        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
          {t('auth.forgot_password')}
        </Link>
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="auth-submit-btn">
        {loading ? t('auth.signing_in') : t('auth.sign_in')}
      </Button>
    </form>
  );

  const renderRegisterForm = () => (
    <form onSubmit={handleSubmit}>
      {renderStepIndicator()}
      {renderStepTitle()}
      
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}

      <div className="flex justify-between mt-6">
        {currentStep > 1 ? (
          <Button type="button" variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {t('common.back')}
          </Button>
        ) : (
          <div></div>
        )}
        
        {currentStep < totalSteps ? (
          <Button type="button" onClick={handleNext} data-testid="next-step-btn">
            {t('common.next')} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button type="submit" disabled={loading} data-testid="auth-submit-btn">
            {loading ? t('auth.creating_account') : t('auth.create_account')}
          </Button>
        )}
      </div>
    </form>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8" data-testid="auth-page">
      <div className="w-full max-w-lg">
        <div className="bg-white border border-slate-200 rounded-md p-8 shadow-sm">
          <div className="text-center mb-6">
            <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {isLogin ? t('auth.welcome_back') : t('auth.join_tradehub')}
            </h1>
            <p className="text-slate-600">
              {isLogin ? t('auth.sign_in_account') : t('auth.create_account_explore')}
            </p>
          </div>

          {isLogin ? renderLoginForm() : renderRegisterForm()}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                resetForm();
              }}
              className="text-sm text-primary hover:underline"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? t('auth.no_account') : t('auth.have_account')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
